import { ITicketWithRelations } from "../../models/tickets";
import { ITICKETCSVFormat } from "../../models/ticket_csv_format";
import { ITicketField } from "../../models/ticket_fields";
import { ICustomerField } from "../../models/customer";

export interface TicketCsvTransformOptions {
  useFixedTicketFields: boolean;
  useFixedUserFields: boolean;
  enableProductLabelCleanup: boolean;
  activeTicketFields?: ITicketField[];
  activeUserFields?: ICustomerField[];
}

export const transformFromTicketToCSV = (
  ticketWithComments: ITicketWithRelations,
  options: TicketCsvTransformOptions,
): ITICKETCSVFormat => {
  const { ticket, comments, requester, assignee } = ticketWithComments;

  const getCustomFieldValue = (fieldId: number | string): string => {
    const field = ticket.custom_fields.find((f) => f.id == fieldId);
    return field ? field.value : "";
  };

  const csv: ITICKETCSVFormat = {
    id: ticket.id,
    subject: ticket.subject,
    assignee_name: assignee?.name || "担当者なし",
    description:
      comments.length > 0 ? comments[0].body.replace(/\n\n/g, "\n") : "",
    comments: comments
      .slice(1)
      .map((comment) => {
        const attachments =
          comment.attachments
            ?.map(
              (attachment) =>
                `${attachment.file_name} ./attachments/${ticket.id}/${attachment.file_name}`,
            )
            .join("\n") || "";
        return `${comment.body.replace(/\n\n/g, "\n")}\n${attachments}`;
      })
      .join("\n---\n"),
    requester_name: requester?.name || "不明",
    requester_email: requester?.email || "",
    requester_phone: requester?.phone
      ? '="' + formatPhoneNumber(requester.phone) + '"' // Excelで先頭0が削除されないよう数式化
      : "",
    created_at: new Date(ticket.created_at)
      .toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/\//g, "/")
      .replace(/,/g, ""),
  };

  if (options.useFixedTicketFields) {
    csv.target_product =
      getCustomFieldValue(import.meta.env.VITE_TARGET_PRODUCT_ID) || "";
    csv.inquiry_type =
      getCustomFieldValue(import.meta.env.VITE_INQUIRY_TYPE_ID) || "";
    csv.customer_phase =
      getCustomFieldValue(import.meta.env.VITE_CUSTOMER_PHASE) || "";
    csv.priority_products =
      getCustomFieldValue(import.meta.env.VITE_PRIORITY_PRODUCTS) || "";
  } else if (options.activeTicketFields) {
    for (const field of options.activeTicketFields) {
      const value = ticket.custom_fields.find((f) => f.id === field.id)?.value;
      csv[`custom_fields.${field.title}`] = formatFieldValue(value, field.type);
    }
  }

  if (options.useFixedUserFields) {
    csv.requester_company = requester?.user_fields?.company || "";
    csv.requester_department = requester?.user_fields?.department || "";
    csv.requester_industry = requester?.user_fields?.industry || "";
    csv.requester_postcode = requester?.user_fields?.postcode || "";
    csv.requester_address = requester?.user_fields?.address || "";
  } else if (options.activeUserFields) {
    for (const field of options.activeUserFields) {
      const value = requester?.user_fields?.[field.key];
      csv[`requester_user_fields.${field.key}`] = formatFieldValue(
        value,
        field.type,
      );
    }
  }

  if (options.enableProductLabelCleanup) {
    if (csv.target_product) {
      csv.target_product = String(csv.target_product).replace(
        /（お問合せ製品）/g,
        "",
      );
    }
    if (csv.inquiry_type) {
      csv.inquiry_type = String(csv.inquiry_type).toUpperCase();
    }
  }

  return csv;
};

const formatFieldValue = (value: unknown, type?: string): string => {
  if (value === null || value === undefined) return "";
  if (type === "checkbox") return value ? "true" : "false";
  if (Array.isArray(value)) return value.join(",");
  return String(value);
};

// 日本の番号(+81で始まる)のみ変換する関数
const formatPhoneNumber = (phoneStr: string) => {
  if (phoneStr.startsWith("+81")) {
    const localNumber = phoneStr.slice(3);
    return "0" + localNumber;
  }

  // その他の国番号はそのまま文字列で返す
  return phoneStr;
};

export const buildTicketCsvHeaders = (
  options: TicketCsvTransformOptions,
): string[] => {
  const baseHeaders = [
    "id",
    "created_at",
    "subject",
    "description",
    "comments",
    "assignee_name",
    "requester_name",
    "requester_email",
    "requester_phone",
  ];

  const ticketFieldHeaders = options.useFixedTicketFields
    ? ["target_product", "inquiry_type", "customer_phase", "priority_products"]
    : (options.activeTicketFields ?? []).map(
        (f) => `custom_fields.${f.title}`,
      );

  const userFieldHeaders = options.useFixedUserFields
    ? [
        "requester_company",
        "requester_department",
        "requester_industry",
        "requester_postcode",
        "requester_address",
      ]
    : (options.activeUserFields ?? []).map(
        (f) => `requester_user_fields.${f.key}`,
      );

  return [...baseHeaders, ...ticketFieldHeaders, ...userFieldHeaders];
};
